import React, { useEffect, useMemo, useState } from "react";
import { getAllAssets, getAssetsByUserToken } from "shared/api/client/orvalHelpers";
import { parseApiError } from "shared/api/client/errorParser";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { normalizeUserRole } from "shared/auth/constants/roles";
import { formatDate } from "shared/utils/formatDate";
import { formatDuration } from "shared/utils/formatDuration";
import { downloadCsv, toCsv } from "shared/utils/csv";
import {
  getRegisteredDevices,
  getVenuePlaylists,
  type RegisteredDevice,
  type VenuePlaylist,
} from "features/device/api/device.api";
import "./AnalyticsDashboard.css";

export type AnalyticsTitle = {
  id: number;
  title: string;
  status: string;
  format: string;
  category: string;
  genre: string;
  durationSeconds: number | null;
  price: number;
  uploadTime: string;
  placements: number;
};

const STATUS_ORDER = ["Published", "UnderReview", "Encoding", "Draft", "Rejected"];
const STATUS_LABELS: Record<string, string> = {
  Published: "Published",
  UnderReview: "Under review",
  Encoding: "Encoding",
  Draft: "Draft",
  Rejected: "Rejected",
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function statusSlug(status: string): string {
  return status.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function mainVideoDuration(asset: Record<string, any>): number | null {
  const videos: any[] = Array.isArray(asset.videoLib)
    ? asset.videoLib
    : Array.isArray(asset.videos)
    ? asset.videos
    : [];
  const main =
    videos.find((v) => String(v?.role ?? "").toLowerCase() === "mainvideo") ?? videos[0];
  const duration = Number(main?.duration);
  return Number.isFinite(duration) && duration > 0 ? duration : null;
}

export function toAnalyticsTitles(
  assets: unknown[],
  playlists: VenuePlaylist[]
): AnalyticsTitle[] {
  const placements = new Map<number, number>();
  playlists.forEach((pl) =>
    pl.items.forEach((item) =>
      placements.set(item.assetId, (placements.get(item.assetId) ?? 0) + 1)
    )
  );
  return assets
    .filter((a): a is Record<string, any> => Boolean(a) && typeof a === "object")
    .map((a) => {
      const id = Number(a.id ?? a.ID ?? a.assetId ?? 0);
      return {
        id,
        title: String(a.title || `Title #${id}`),
        status: String(a.status || "Draft"),
        format: String(a.format || "Unspecified"),
        category: String(a.category || "Uncategorized"),
        genre: String(a.genre || ""),
        durationSeconds: mainVideoDuration(a),
        price: Number(a.price) || 0,
        uploadTime: String(a.uploadTime ?? a.upload_time ?? ""),
        placements: placements.get(id) ?? 0,
      };
    });
}

function countBy<T>(items: T[], key: (item: T) => string): Array<[string, number]> {
  const counts = new Map<string, number>();
  items.forEach((item) => {
    const k = key(item);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  return Array.from(counts.entries());
}

const BreakdownBars: React.FC<{
  rows: Array<[string, number]>;
  total: number;
  labelFor?: (key: string) => string;
  variant?: "status" | "plain";
  emptyText: string;
}> = ({ rows, total, labelFor = (k) => k, variant = "plain", emptyText }) => {
  if (rows.length === 0) return <p className="analytics-empty">{emptyText}</p>;
  return (
    <div>
      {rows.map(([key, count]) => {
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div className="analytics-bar-row" key={key} data-testid="analytics-bar">
            <span className="analytics-bar-label" title={labelFor(key)}>
              {labelFor(key)}
            </span>
            <div
              className="analytics-bar-track"
              role="progressbar"
              aria-label={labelFor(key)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <div
                className={`analytics-bar-fill${variant === "status" ? ` analytics-bar-fill--${statusSlug(key)}` : ""}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="analytics-bar-count">
              {count} · {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const AnalyticsDashboard: React.FC = () => {
  const [titles, setTitles] = useState<AnalyticsTitle[]>([]);
  const [playlists, setPlaylists] = useState<VenuePlaylist[]>([]);
  const [devices, setDevices] = useState<RegisteredDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    let active = true;
    const role = normalizeUserRole(getCookie("userRole"));
    const loadAssets = role === "CREATOR" ? getAssetsByUserToken : getAllAssets;

    Promise.allSettled([loadAssets(), getVenuePlaylists(), getRegisteredDevices()]).then(
      ([assetsResult, playlistsResult, devicesResult]) => {
        if (!active) return;
        const problems: string[] = [];
        const loadedPlaylists =
          playlistsResult.status === "fulfilled" ? playlistsResult.value : [];
        if (playlistsResult.status === "rejected") {
          problems.push(parseApiError(playlistsResult.reason, "Could not load venue playlists."));
        }
        if (devicesResult.status === "fulfilled") {
          setDevices(devicesResult.value);
        } else {
          problems.push(parseApiError(devicesResult.reason, "Could not load registered headsets."));
        }
        if (assetsResult.status === "fulfilled") {
          const list = Array.isArray(assetsResult.value) ? assetsResult.value : [];
          setTitles(toAnalyticsTitles(list, loadedPlaylists));
        } else {
          problems.push(parseApiError(assetsResult.reason, "Could not load your titles."));
        }
        setPlaylists(loadedPlaylists);
        setErrors(problems);
        setLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, []);

  const kpis = useMemo(() => {
    const published = titles.filter((t) => t.status === "Published").length;
    const pipeline = titles.filter((t) => t.status === "UnderReview" || t.status === "Encoding").length;
    const activeLicenses = playlists.reduce((sum, pl) => sum + (pl.activeLicenseCount || 0), 0);
    return [
      { label: "Total Titles", value: titles.length, hint: "All uploads in your library" },
      { label: "Published Titles", value: published, hint: "Live in the Poetics Stream catalog" },
      { label: "In Review / Encoding", value: pipeline, hint: "Awaiting curator or transcoder" },
      { label: "Venue Playlists", value: playlists.length, hint: "Screening programmes" },
      { label: "Active Screening Licenses", value: activeLicenses, hint: "Unexpired offline licenses" },
      { label: "Registered Headsets", value: devices.length, hint: "Provisionable devices" },
    ];
  }, [titles, playlists, devices]);

  const statusRows = useMemo(() => {
    const rows = countBy(titles, (t) => t.status);
    return rows.sort(([a], [b]) => {
      const ia = STATUS_ORDER.indexOf(a);
      const ib = STATUS_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
    });
  }, [titles]);

  const formatRows = useMemo(
    () => countBy(titles, (t) => t.format).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
    [titles]
  );

  const statusOptions = useMemo(() => statusRows.map(([status]) => status), [statusRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return titles.filter((t) => {
      const matchesStatus = !statusFilter || t.status === statusFilter;
      const matchesSearch =
        !q ||
        t.title.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.genre.toLowerCase().includes(q) ||
        t.format.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [titles, search, statusFilter]);

  const exportCsv = () => {
    const csv = toCsv(
      ["Title", "Status", "Format", "Category", "Genre", "Duration (min)", "Price", "Playlist placements", "Uploaded"],
      filtered.map((t) => [
        t.title,
        statusLabel(t.status),
        t.format,
        t.category,
        t.genre,
        t.durationSeconds === null ? "" : Math.round(t.durationSeconds / 60),
        t.price > 0 ? t.price.toFixed(2) : "Free",
        t.placements,
        t.uploadTime,
      ])
    );
    downloadCsv(csv, `poetics-stream-analytics-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="library-creator-content analytics-page-wrapper">
      <div className="main-library">
        <div className="top-container-library">
          <div className="left-top-container-library">
            <div className="title-top-container-library">Analytics</div>
            <div className="subtitle-top-container-library">
              Creator &amp; venue performance across your catalog, screening playlists and headsets.
            </div>
          </div>
        </div>

        <section className="analytics-dashboard" aria-label="Creator and venue analytics">
          {errors.map((text) => (
            <div className="analytics-message error" role="alert" key={text}>
              {text}
            </div>
          ))}

          <div className="analytics-kpi-grid" data-testid="analytics-kpis">
            {kpis.map((kpi) => (
              <div className="analytics-kpi" key={kpi.label} data-testid="analytics-kpi">
                <div className="analytics-kpi-label">{kpi.label}</div>
                <div className="analytics-kpi-value" data-testid={`analytics-kpi-value-${kpi.label}`}>{loading ? "…" : kpi.value}</div>
                <div className="analytics-kpi-hint">{kpi.hint}</div>
              </div>
            ))}
          </div>

          <div className="analytics-panel-grid">
            <div className="analytics-panel">
              <h3>Catalog Status Breakdown</h3>
              <BreakdownBars
                rows={statusRows}
                total={titles.length}
                labelFor={statusLabel}
                variant="status"
                emptyText={loading ? "Loading…" : "No titles yet."}
              />
            </div>
            <div className="analytics-panel">
              <h3>Format Breakdown</h3>
              <BreakdownBars
                rows={formatRows}
                total={titles.length}
                emptyText={loading ? "Loading…" : "No titles yet."}
              />
            </div>
          </div>

          <div className="analytics-toolbar">
            <h3 style={{ margin: 0, flex: "1 1 auto" }}>Title Performance</h3>
            <input
              type="search"
              placeholder="Search titles, categories, formats…"
              aria-label="Search titles"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="analytics-btn"
              onClick={exportCsv}
              disabled={filtered.length === 0}
            >
              Export Analytics CSV
            </button>
          </div>

          <div className="analytics-table-wrapper">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Format</th>
                  <th>Category</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th>Playlist placements</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="analytics-empty">
                      {loading ? "Loading titles…" : "No titles match the current filters."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => (
                    <tr key={t.id} data-testid="analytics-title-row">
                      <td>{t.title}</td>
                      <td>
                        <span className={`analytics-status-pill analytics-status-pill--${statusSlug(t.status)}`}>
                          {statusLabel(t.status)}
                        </span>
                      </td>
                      <td>{t.format}</td>
                      <td>{t.category}</td>
                      <td>{t.durationSeconds === null ? "—" : formatDuration(t.durationSeconds)}</td>
                      <td>{t.price > 0 ? `€${t.price.toFixed(2)}` : "Free"}</td>
                      <td>{t.placements}</td>
                      <td>{formatDate(t.uploadTime) || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
